/** Pure helpers for Source Compare UI labels and comparison summaries. */

export function formatShortSha(sha, length = 7) {
  const value = String(sha ?? "").trim();
  if (!value) return "—";
  return value.length <= length ? value : value.slice(0, length);
}

export function formatSnapshotDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function snapshotHasSourceMetadata(snapshot) {
  const source = snapshot?.source;
  if (!source) return false;
  return Boolean(source.ref || source.branch || source.commitSha);
}

export function hasLegacySnapshotsWithoutSource(snapshots = []) {
  return snapshots.some((snapshot) => !snapshotHasSourceMetadata(snapshot));
}

export function getSnapshotSortKey(snapshot) {
  const value = snapshot?.generatedAt ?? snapshot?.createdAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

/**
 * Display label for a snapshot option.
 * Priority: ref → branch → commitSha → analysisRunId → Unknown source
 */
export function formatSnapshotSourceLabel(snapshot) {
  const source = snapshot?.source ?? {};
  const date = formatSnapshotDate(snapshot?.generatedAt ?? snapshot?.createdAt);
  const sha = formatShortSha(source.commitSha);
  const maps = snapshot?.coverage?.engineeringMapCount ?? "—";
  const files = snapshot?.fileInventory?.totalFiles ?? "—";

  let primary = "Unknown source";
  if (source.ref) {
    primary = source.ref;
  } else if (source.branch) {
    primary = source.branch;
  } else if (source.defaultBranch) {
    primary = source.defaultBranch;
  } else if (source.commitSha) {
    primary = `commit ${sha}`;
  } else if (snapshot?.analysisRunId) {
    primary = `analysis run ${formatShortSha(snapshot.analysisRunId, 8)} · legacy`;
  }

  return `${primary} · ${sha} · ${date} · ${files} files · ${maps} maps`;
}

export function effectiveSourceStatus(item, live) {
  if (live?.status === "failed" || live?.finalizeStatus === "failed") return "failed";
  if (live?.status === "completed") return "ready";
  if (live?.status === "processing" || live?.status === "pending") {
    return live.status === "pending" ? "queued" : "processing";
  }
  return item?.status || "unknown";
}

export function formatSourceStatus(status) {
  if (status === "completed" || status === "ready") return "Ready";
  if (status === "processing") return "Analyzing";
  if (status === "pending" || status === "queued") return "Queued";
  if (status === "failed") return "Failed";
  return status || "Unknown";
}

export function snapshotPrimaryName(snapshot) {
  return formatSnapshotSourceLabel(snapshot).split(" · ")[0];
}

/**
 * Customer-facing list of sources: completed snapshots plus in-progress analyses.
 */
function looksLikeCommitSha(value, commitSha) {
  const name = String(value ?? "").trim().toLowerCase();
  if (!name) return true;
  const sha = String(commitSha ?? "").trim().toLowerCase();
  if (sha && (name === sha || sha.startsWith(name))) return true;
  return /^[0-9a-f]{7,40}$/i.test(name);
}

export function branchDisplayNameFromSource(source = {}) {
  const src = source && typeof source === "object" ? source : {};
  for (const candidate of [
    src.requestedBranch,
    src.branch,
    src.ref,
    src.resolvedRef,
    src.defaultBranch,
  ]) {
    const name = String(candidate ?? "").trim();
    if (!name || name === "default" || looksLikeCommitSha(name, src.commitSha)) {
      continue;
    }
    return name;
  }
  return "";
}

export function isWeakBranchDisplayName(name, repo) {
  const n = String(name ?? "").trim();
  if (!n || n === "default" || n === "unknown branch") return true;
  if (repo?.owner && repo?.name && n === `${repo.owner}/${repo.name}`) return true;
  return false;
}

export function branchLabelFromRepo(repo) {
  return (
    branchDisplayNameFromSource(repo?.analysisSource) ||
    (repo?.owner && repo?.name ? `${repo.owner}/${repo.name}` : "unknown branch")
  );
}

export function formatBranchSelectorLabel(analysis, { isStartingAnalysis = false } = {}) {
  const name = String(analysis?.branch || "").trim() || "unknown branch";
  const status = formatSourceStatus(analysis?.status);
  const starting = isStartingAnalysis ? " · starting analysis" : "";
  return `${name}${starting} — ${status}${analysis?.stale ? " (stale)" : ""}`;
}

export function mergeBranchAnalyses(existing = [], discovered = [], repo = null) {
  const byId = new Map();
  for (const analysis of existing || []) {
    if (!analysis?.repoId) continue;
    byId.set(String(analysis.repoId), { ...analysis });
  }
  for (const item of discovered || []) {
    if (!item?.repoId) continue;
    const id = String(item.repoId);
    const prev = byId.get(id) || {};
    const discoveredName =
      branchDisplayNameFromSource({
        requestedBranch: item.branch || item.title,
        commitSha: item.commitSha,
      }) || item.branch || item.title;
    const prevName = String(prev.branch || "").trim();
    const keepPrev = prevName && !isWeakBranchDisplayName(prevName, repo);
    byId.set(id, {
      ...prev,
      repoId: id,
      branch: keepPrev ? prevName : discoveredName || prevName || "unknown branch",
      status: item.status === "ready" ? "completed" : item.status || prev.status || "unknown",
      commitSha: item.commitSha || prev.commitSha || null,
    });
  }
  return Array.from(byId.values());
}

export function branchAnalysesEqual(left = [], right = []) {
  if (left.length !== right.length) return false;
  const byId = new Map(left.map((item) => [String(item.repoId), item]));
  for (const item of right) {
    const prev = byId.get(String(item.repoId));
    if (!prev) return false;
    if (
      prev.branch !== item.branch ||
      prev.status !== item.status ||
      (prev.commitSha || null) !== (item.commitSha || null)
    ) {
      return false;
    }
  }
  return true;
}

export function buildSourceCompareItems({
  snapshots = [],
  branchAnalyses = [],
  pending = null,
  canonical = null,
} = {}) {
  const items = [];
  const seenRepoIds = new Set();
  const seenSnapshotIds = new Set();

  for (const snapshot of snapshots) {
    if (!snapshot?.id || seenSnapshotIds.has(snapshot.id)) continue;
    seenSnapshotIds.add(snapshot.id);
    if (snapshot.repoId) seenRepoIds.add(String(snapshot.repoId));
    items.push({
      key: `snapshot:${snapshot.id}`,
      kind: "snapshot",
      snapshotId: snapshot.id,
      repoId: snapshot.repoId || null,
      title: snapshotPrimaryName(snapshot),
      detail: formatSnapshotSourceLabel(snapshot),
      status: "ready",
      commitSha: snapshot.source?.commitSha || null,
      comparable: true,
    });
  }

  if (canonical?.repoId && !seenRepoIds.has(String(canonical.repoId))) {
    const repoId = String(canonical.repoId);
    seenRepoIds.add(repoId);
    items.push({
      key: `canonical:${repoId}`,
      kind: "analysis",
      snapshotId: null,
      repoId,
      title: canonical.title || "Default analysis",
      detail: canonical.detail || formatSourceStatus(canonical.status || "ready"),
      status: canonical.status === "completed" ? "ready" : canonical.status || "ready",
      commitSha: canonical.commitSha || null,
      comparable: false,
    });
  }

  for (const analysis of branchAnalyses) {
    const repoId = analysis?.repoId ? String(analysis.repoId) : "";
    if (!repoId || seenRepoIds.has(repoId)) continue;
    seenRepoIds.add(repoId);
    const status = analysis.status === "completed" ? "ready" : analysis.status || "pending";
    items.push({
      key: `analysis:${repoId}`,
      kind: "analysis",
      snapshotId: null,
      repoId,
      title: analysis.branch || "Branch analysis",
      detail: analysis.commitSha
        ? `${formatShortSha(analysis.commitSha)} · ${formatSourceStatus(status)}`
        : formatSourceStatus(status),
      status,
      commitSha: analysis.commitSha || null,
      comparable: false,
    });
  }

  const pendingRepoId = pending?.repoId ? String(pending.repoId) : "";
  if (pendingRepoId && !seenRepoIds.has(pendingRepoId)) {
    const ref = pending.preflight?.headPin?.ref || pending.sourceInput || "Added source";
    items.push({
      key: `pending:${pendingRepoId}`,
      kind: "pending",
      snapshotId: null,
      repoId: pendingRepoId,
      title: ref,
      detail: pending.preflight?.headPin?.sha
        ? `${formatShortSha(pending.preflight.headPin.sha)} · ${formatSourceStatus(pending.phase)}`
        : formatSourceStatus(pending.phase),
      status: pending.phase === "completed" ? "ready" : pending.phase || "queued",
      commitSha: pending.preflight?.headPin?.sha || null,
      comparable: false,
    });
  }

  return items;
}

export function pickDefaultBaseHeadSnapshotIds(snapshots = []) {
  if (snapshots.length < 2) {
    return { baseSnapshotId: "", headSnapshotId: "" };
  }

  const sorted = [...snapshots].sort(
    (a, b) => getSnapshotSortKey(a) - getSnapshotSortKey(b)
  );
  const base = sorted[0];
  const head = sorted[sorted.length - 1];

  if (base?.id === head?.id) {
    return { baseSnapshotId: base?.id ?? "", headSnapshotId: "" };
  }

  return {
    baseSnapshotId: base?.id ?? "",
    headSnapshotId: head?.id ?? "",
  };
}

export function formatSourceMetadataLines(snapshot) {
  const source = snapshot?.source ?? {};
  const lines = [];

  if (source.ref) lines.push({ label: "ref", value: source.ref });
  if (source.branch) lines.push({ label: "branch", value: source.branch });
  if (source.commitSha) {
    lines.push({ label: "commit", value: formatShortSha(source.commitSha, 10) });
  }
  if (source.defaultBranch) {
    lines.push({ label: "default branch", value: source.defaultBranch });
  }
  if (snapshot?.analysisRunId) {
    lines.push({ label: "analysis run", value: formatShortSha(snapshot.analysisRunId, 10) });
  }
  lines.push({
    label: "generated",
    value: formatSnapshotDate(snapshot?.generatedAt ?? snapshot?.createdAt),
  });
  lines.push({
    label: "files",
    value: String(snapshot?.fileInventory?.totalFiles ?? "—"),
  });
  lines.push({
    label: "engineering maps",
    value: String(snapshot?.coverage?.engineeringMapCount ?? "—"),
  });

  return lines;
}

export function formatComparisonHeadline(beforeSnapshot, afterSnapshot, comparison) {
  const beforeLabel = formatSnapshotSourceLabel(beforeSnapshot).split(" · ")[0];
  const afterLabel = formatSnapshotSourceLabel(afterSnapshot).split(" · ")[0];
  const summary = comparison?.summary ?? {};
  const beforeFiles = beforeSnapshot?.fileInventory?.totalFiles ?? comparison?.inventoryChanges?.totalFiles?.before ?? "—";
  const afterFiles = afterSnapshot?.fileInventory?.totalFiles ?? comparison?.inventoryChanges?.totalFiles?.after ?? "—";
  const beforeMaps = beforeSnapshot?.coverage?.engineeringMapCount ?? "—";
  const afterMaps = afterSnapshot?.coverage?.engineeringMapCount ?? "—";
  const riskDelta = summary.riskScoreDelta ?? 0;
  const riskText =
    riskDelta === 0 ? "unchanged" : riskDelta > 0 ? `+${riskDelta}` : String(riskDelta);

  return {
    title: `Compared ${beforeLabel} to ${afterLabel}`,
    filesLine: `Files: ${beforeFiles} → ${afterFiles}`,
    mapsLine: `Engineering maps: ${beforeMaps} → ${afterMaps}`,
    riskLine: `Risk score: ${riskText}`,
  };
}

export function formatNumericDelta(delta) {
  if (!delta || typeof delta !== "object") return null;
  const { before, after, delta: change } = delta;
  if (change === 0) return null;
  const sign = change > 0 ? "+" : "";
  return `${before} → ${after} (${sign}${change})`;
}

function compactSectionLines(lines, emptyMessage) {
  const filtered = lines.filter(Boolean);
  if (!filtered.length) return [emptyMessage];
  return filtered;
}

function surfaceDiffCount(diff) {
  return (diff?.added?.length ?? 0) + (diff?.removed?.length ?? 0);
}

function collectSurfaceFilePaths(surfaceChanges = {}) {
  const paths = new Set();
  for (const diff of Object.values(surfaceChanges)) {
    for (const item of [...(diff?.added ?? []), ...(diff?.removed ?? [])]) {
      const filePath = item?.filePath ?? item?.path;
      if (filePath) paths.add(String(filePath));
    }
  }
  return [...paths];
}

function countChangedSurfaceCategories(surfaceChanges = {}) {
  const categories = [
    surfaceChanges.exports,
    surfaceChanges.routes,
    surfaceChanges.envVars,
    surfaceChanges.dataModels,
    surfaceChanges.sideEffects,
  ];
  return categories.filter((diff) => surfaceDiffCount(diff) > 0).length;
}

function hasMeaningfulComparisonChanges(comparison) {
  if (!comparison) return false;
  const summary = comparison.summary ?? {};
  if ((summary.filesAdded ?? 0) > 0 || (summary.filesRemoved ?? 0) > 0) return true;
  if ((summary.newHighRiskFiles ?? 0) > 0 || (summary.resolvedHighRiskFiles ?? 0) > 0) return true;
  if ((summary.riskScoreDelta ?? 0) !== 0) return true;
  if ((summary.engineeringMapCoverageDelta ?? 0) !== 0) return true;
  if ((summary.chunkCountDelta ?? 0) !== 0 || (summary.summaryChunkCountDelta ?? 0) !== 0) return true;
  if (summary.analysisQualityImproved || summary.analysisQualityRegressed) return true;
  if (countChangedSurfaceCategories(comparison.surfaceChanges) > 0) return true;
  return false;
}

function primarySourceLabel(snapshot) {
  return formatSnapshotSourceLabel(snapshot).split(" · ")[0];
}

export function buildSourceCompareInterpretation(comparison, beforeSnapshot, afterSnapshot) {
  const summary = comparison?.summary ?? {};
  const surfaceChanges = comparison?.surfaceChanges ?? {};
  const beforeLabel = primarySourceLabel(beforeSnapshot);
  const afterLabel = primarySourceLabel(afterSnapshot);
  const beforeFiles = beforeSnapshot?.fileInventory?.totalFiles ?? comparison?.inventoryChanges?.totalFiles?.before ?? "—";
  const afterFiles = afterSnapshot?.fileInventory?.totalFiles ?? comparison?.inventoryChanges?.totalFiles?.after ?? "—";
  const beforeMaps = beforeSnapshot?.coverage?.engineeringMapCount ?? "—";
  const afterMaps = afterSnapshot?.coverage?.engineeringMapCount ?? "—";
  const riskDelta = summary.riskScoreDelta ?? 0;
  const filesAdded = summary.filesAdded ?? 0;
  const filesRemoved = summary.filesRemoved ?? 0;
  const exportsChanged = surfaceDiffCount(surfaceChanges.exports);
  const routesChanged = surfaceDiffCount(surfaceChanges.routes);
  const envChanged = surfaceDiffCount(surfaceChanges.envVars);
  const modelsChanged = surfaceDiffCount(surfaceChanges.dataModels);
  const sideEffectsChanged = surfaceDiffCount(surfaceChanges.sideEffects);
  const surfaceCategoriesChanged = countChangedSurfaceCategories(surfaceChanges);
  const coverageChanged =
    (comparison?.coverageChanges?.engineeringMapCount?.delta ?? 0) !== 0 ||
    (comparison?.coverageChanges?.chunkCount?.delta ?? 0) !== 0 ||
    (summary.engineeringMapCoverageDelta ?? 0) !== 0 ||
    summary.analysisQualityImproved ||
    summary.analysisQualityRegressed;

  const reviewFirstPaths = collectSurfaceFilePaths(surfaceChanges).slice(0, 3);
  const reviewFirst =
    reviewFirstPaths.length > 0 ? reviewFirstPaths : ["Changed surfaces"];

  let changeType = "Mixed change";
  let highlights = [];

  const hasFileStructureChange = filesAdded > 0 || filesRemoved > 0;
  const hasRiskIncrease = (summary.newHighRiskFiles ?? 0) > 0 || riskDelta > 0;
  const hasSurfaceChange = surfaceCategoriesChanged > 0;
  const majorChangeCount = [
    hasFileStructureChange,
    hasRiskIncrease,
    hasSurfaceChange,
    coverageChanged,
  ].filter(Boolean).length;

  if (!hasMeaningfulComparisonChanges(comparison)) {
    changeType = "No meaningful changes";
    highlights = ["File inventory, coverage, risk, and tracked surfaces appear unchanged."];
  } else if (majorChangeCount >= 2) {
    changeType = "Mixed change";
    if (hasFileStructureChange) highlights.push(`${filesAdded} file(s) added, ${filesRemoved} removed`);
    if (hasSurfaceChange) highlights.push("Engineering surfaces changed");
    if (hasRiskIncrease) highlights.push("Risk profile changed");
    if (coverageChanged) highlights.push("Analysis coverage or quality changed");
  } else if (hasRiskIncrease) {
    changeType = "Risk profile change";
    highlights = [
      `${summary.newHighRiskFiles ?? 0} new high-risk file(s)`,
      riskDelta !== 0 ? `Risk score delta: ${riskDelta > 0 ? "+" : ""}${riskDelta}` : null,
    ].filter(Boolean);
  } else if (hasFileStructureChange) {
    changeType = "File structure change";
    highlights = [`${filesAdded} file(s) added`, `${filesRemoved} file(s) removed`];
  } else if (hasSurfaceChange) {
    changeType = "API surface change";
    if (exportsChanged) highlights.push("Exported symbols changed");
    if (routesChanged) highlights.push("HTTP/API routes changed");
    if (envChanged) highlights.push("Configuration/environment surface changed");
    if (modelsChanged) highlights.push("Data models changed");
    if (sideEffectsChanged) highlights.push("Runtime side effects changed");
  } else if (coverageChanged) {
    changeType = "Analysis coverage change";
    highlights = ["Engineering map or analysis quality metrics changed"];
  }

  let scope = "Moderate";
  if (changeType === "No meaningful changes") {
    scope = "None";
  } else if (
    filesAdded + filesRemoved === 0 &&
    surfaceCategoriesChanged <= 1 &&
    (summary.newHighRiskFiles ?? 0) === 0 &&
    riskDelta === 0
  ) {
    scope = "Narrow";
  } else if (
    filesAdded + filesRemoved >= 3 ||
    surfaceCategoriesChanged >= 3 ||
    ((summary.newHighRiskFiles ?? 0) > 0 && filesAdded + filesRemoved > 0)
  ) {
    scope = "Broad";
  }

  let riskMovement = "Unchanged";
  if (riskDelta > 0 || (summary.newHighRiskFiles ?? 0) > 0) {
    riskMovement = "Increased";
  } else if (riskDelta < 0 || (summary.resolvedHighRiskFiles ?? 0) > 0) {
    riskMovement = "Decreased";
  }

  let reviewPriority = "Medium";
  if (changeType === "No meaningful changes") {
    reviewPriority = "Low";
  } else if (
    (summary.newHighRiskFiles ?? 0) > 0 ||
    riskDelta >= 2 ||
    envChanged > 0 ||
    routesChanged > 0 ||
    sideEffectsChanged > 0
  ) {
    reviewPriority = "High";
  } else if (exportsChanged > 0 || filesAdded > 0 || filesRemoved > 0 || summary.analysisQualityRegressed) {
    reviewPriority = "Medium";
  } else if (coverageChanged && surfaceCategoriesChanged === 0) {
    reviewPriority = "Low";
  }

  let summaryText = "";
  if (changeType === "No meaningful changes") {
    summaryText =
      "No meaningful repo-level changes were detected between these snapshots. File inventory, engineering map coverage, risk score, and tracked engineering surfaces appear unchanged.";
  } else if (changeType === "API surface change" && exportsChanged && filesAdded + filesRemoved === 0) {
    const mainFile = reviewFirstPaths[0] ?? "the changed source file";
    summaryText =
      `This comparison shows a focused API surface change between ${beforeLabel} and ${afterLabel}. ` +
      `The repo structure did not change: both snapshots have ${beforeFiles} files, ${beforeMaps} engineering maps, and the same risk score. ` +
      `The meaningful difference is in ${mainFile}, where the exported API changed. ` +
      `${summary.exportsAdded ?? 0} export(s) were added and ${summary.exportsRemoved ?? 0} export(s) were removed.`;
  } else if (changeType === "Risk profile change") {
    summaryText =
      `This comparison shows a risk profile change between ${beforeLabel} and ${afterLabel}. ` +
      `Review newly flagged high-risk files and any related runtime or configuration surfaces before relying on this source.`;
  } else if (changeType === "File structure change") {
    summaryText =
      `This comparison shows a file structure change between ${beforeLabel} and ${afterLabel}. ` +
      `${filesAdded} file(s) were added and ${filesRemoved} file(s) were removed while overall repo coverage may still look similar.`;
  } else if (changeType === "Analysis coverage change") {
    summaryText =
      `This comparison mainly reflects analysis coverage or quality differences between ${beforeLabel} and ${afterLabel}. ` +
      `That may reflect analyzer output rather than a definite code behavior change.`;
  } else {
    summaryText =
      `This comparison shows multiple kinds of changes between ${beforeLabel} and ${afterLabel}. ` +
      `Files: ${beforeFiles} → ${afterFiles}. Engineering maps: ${beforeMaps} → ${afterMaps}. ` +
      `Review the detailed sections below for evidence.`;
  }

  return {
    changeType,
    scope,
    riskMovement,
    reviewPriority,
    summary: summaryText,
    reviewFirst,
    highlights,
  };
}

export function capItems(items = [], limit = 10) {
  const list = Array.isArray(items) ? items : [];
  const visible = list.slice(0, limit);
  const remaining = Math.max(0, list.length - visible.length);
  return { visible, remaining };
}

export function formatSurfaceItem(item) {
  if (!item) return "";
  const name = item.name ?? item.filePath ?? "item";
  const filePath = item.filePath && item.name ? ` (${item.filePath})` : item.filePath ? item.filePath : "";
  return `${name}${filePath}`;
}

export function buildComparisonSections(comparison) {
  if (!comparison) return [];

  const summary = comparison.summary ?? {};
  const filesAdded = summary.filesAdded ?? 0;
  const filesRemoved = summary.filesRemoved ?? 0;
  const riskDelta = summary.riskScoreDelta ?? 0;
  const sections = [];

  sections.push({
    id: "inventory",
    title: "Inventory",
    lines: compactSectionLines(
      [
        filesAdded > 0 ? `Files added: ${filesAdded}` : null,
        filesRemoved > 0 ? `Files removed: ${filesRemoved}` : null,
        formatNumericDelta(comparison.inventoryChanges?.totalFiles),
      ],
      `No inventory changes. Both snapshots have ${comparison.inventoryChanges?.totalFiles?.before ?? summary.fileCountDelta ?? "the same"} files.`
    ),
  });

  const beforeMaps = comparison.coverageChanges?.engineeringMapCount?.before ?? "—";
  sections.push({
    id: "coverage",
    title: "Coverage",
    lines: compactSectionLines(
      [
        formatNumericDelta(comparison.coverageChanges?.engineeringMapCount),
        formatNumericDelta(comparison.coverageChanges?.chunkCount),
        formatNumericDelta(comparison.coverageChanges?.summaryChunkCount),
        summary.engineeringMapCoverageDelta
          ? `Engineering map coverage delta: ${summary.engineeringMapCoverageDelta}`
          : null,
      ],
      `No coverage changes. Both snapshots have ${beforeMaps} engineering maps.`
    ),
  });

  const surfaceLines = [];
  const surfacePairs = [
    ["Exports", comparison.surfaceChanges?.exports],
    ["Routes", comparison.surfaceChanges?.routes],
    ["Env vars", comparison.surfaceChanges?.envVars],
    ["Data models", comparison.surfaceChanges?.dataModels],
    ["Side effects", comparison.surfaceChanges?.sideEffects],
  ];
  for (const [label, diff] of surfacePairs) {
    const added = diff?.added?.length ?? 0;
    const removed = diff?.removed?.length ?? 0;
    if (added || removed) {
      surfaceLines.push(`${label}: +${added}/-${removed}`);
    }
  }
  sections.push({
    id: "surfaces",
    title: "Surfaces",
    lines:
      surfaceLines.length > 0
        ? surfaceLines
        : ["No surface changes detected in exports, routes, env vars, data models, or side effects."],
    details: surfacePairs
      .map(([label, diff]) => ({
        label,
        added: capItems(diff?.added ?? []).visible.map(formatSurfaceItem),
        removed: capItems(diff?.removed ?? []).visible.map(formatSurfaceItem),
        addedRemaining: capItems(diff?.added ?? []).remaining,
        removedRemaining: capItems(diff?.removed ?? []).remaining,
      }))
      .filter((entry) => entry.added.length || entry.removed.length),
  });

  const newHighRisk = summary.newHighRiskFiles ?? 0;
  const resolvedHighRisk = summary.resolvedHighRiskFiles ?? 0;
  sections.push({
    id: "risk",
    title: "Risk",
    lines: compactSectionLines(
      [
        newHighRisk > 0 ? `New high-risk files: ${newHighRisk}` : null,
        resolvedHighRisk > 0 ? `Resolved high-risk files: ${resolvedHighRisk}` : null,
        riskDelta !== 0 ? `Risk score delta: ${riskDelta}` : null,
      ],
      "No risk profile changes detected."
    ),
    details: [
      {
        label: "New high-risk files",
        items: capItems(comparison.riskChanges?.newHighRiskFiles ?? []).visible.map(
          (file) => file.filePath ?? file.path ?? String(file)
        ),
        remaining: capItems(comparison.riskChanges?.newHighRiskFiles ?? []).remaining,
      },
      {
        label: "Resolved high-risk files",
        items: capItems(comparison.riskChanges?.resolvedHighRiskFiles ?? []).visible.map(
          (file) => file.filePath ?? file.path ?? String(file)
        ),
        remaining: capItems(comparison.riskChanges?.resolvedHighRiskFiles ?? []).remaining,
      },
    ].filter((entry) => entry.items.length > 0),
  });

  sections.push({
    id: "important-files",
    title: "Important files",
    lines: compactSectionLines(
      [
        (comparison.importantFileChanges?.entrypoints?.added?.length ?? 0) > 0
          ? `Entrypoints added: ${comparison.importantFileChanges.entrypoints.added.length}`
          : null,
        (comparison.importantFileChanges?.entrypoints?.removed?.length ?? 0) > 0
          ? `Entrypoints removed: ${comparison.importantFileChanges.entrypoints.removed.length}`
          : null,
        (comparison.importantFileChanges?.tests?.added?.length ?? 0) > 0
          ? `Tests added: ${comparison.importantFileChanges.tests.added.length}`
          : null,
        (comparison.importantFileChanges?.tests?.removed?.length ?? 0) > 0
          ? `Tests removed: ${comparison.importantFileChanges.tests.removed.length}`
          : null,
        (comparison.importantFileChanges?.highRisk?.added?.length ?? 0) > 0
          ? `High-risk important files added: ${comparison.importantFileChanges.highRisk.added.length}`
          : null,
        (comparison.importantFileChanges?.highRisk?.removed?.length ?? 0) > 0
          ? `High-risk important files removed: ${comparison.importantFileChanges.highRisk.removed.length}`
          : null,
      ],
      "No important file category changes detected."
    ),
  });

  sections.push({
    id: "quality",
    title: "Quality",
    lines: compactSectionLines(
      [
        summary.analysisQualityImproved ? "Analysis quality improved: yes" : null,
        summary.analysisQualityRegressed ? "Analysis quality regressed: yes" : null,
        formatNumericDelta(comparison.analysisQualityChanges?.degradedEngineeringMaps),
        formatNumericDelta(comparison.analysisQualityChanges?.partialEngineeringMaps),
      ],
      "No analysis quality changes detected."
    ),
  });

  return sections;
}
